import React, { useMemo, useRef } from 'react';
import {
	Stack,
	Typography,
	Dialog,
	TableContainer,
	Table,
	TableCell,
	TableRow,
	TableHead,
	TableBody,
	Divider,
	Box
} from '@mui/material';
import { isEmpty } from 'lodash';
import * as XLSX from 'xlsx/xlsx.mjs';
import NoData from '../NoData';
import { EncabezadoOrdenable, useOrdenTabla } from '../../helpers/OrdenTabla';
import { EncabezadoReporte, HojaImpresion } from './EncabezadoReporte';

/**
 * Esqueleto comun de los reportes de cartera agrupados: encabezado, exportacion a
 * Excel, impresion, subtotales por grupo y total general.
 *
 * El encabezado es el comun de los reportes (EncabezadoReporte en pantalla,
 * HojaImpresion en papel); las tablas se dibujan una vez y se usan en ambos.
 *
 * Cada reporte solo aporta sus datos y la definicion de columnas; la tabla, el
 * Excel y los totales salen de esa misma definicion, de modo que agregar una
 * columna sea un solo cambio y no se desincronice lo que se ve con lo que se
 * exporta.
 *
 * Forma de `columnas`:
 *   { key, label, align, total?, formatoTotal?, valor: (fila) => nodo,
 *     excel?: (fila) => valor }
 *   - `total: true` suma la columna por grupo y en el total general.
 *   - `formatoTotal` da formato a esa suma (por defecto se muestra cruda).
 *   - `excel` devuelve el valor crudo (numero/fecha) para que Excel no lo trate
 *     como texto. Si se omite se usa `valor`.
 *   - `excelColumnas: (fila) => ({ etiqueta: valor, ... })` reemplaza a `excel` cuando
 *     una celda reúne varios datos que en Excel van en columnas separadas.
 *   - `unaVezPorId: true` suma la columna una sola vez por `fila.id`: para los
 *     montos del credito cuando un mismo credito sale en varias filas.
 *   - Orden: clic en el encabezado (helpers/OrdenTabla). `orden` es el campo de
 *     la fila por el que se ordena (por defecto `key`), `texto: true` lo compara
 *     como palabras y `ordenable: false` deja el encabezado fijo.
 *
 * `agruparPor` recibe una fila y devuelve el nombre del grupo. `ordenInicial` es
 * el campo por el que sale ordenado; el orden se aplica dentro de cada grupo.
 *
 * Sin `agruparPor` el reporte es una sola tabla: sin titulo de grupo ni subtotal, y al
 * pie solo el TOTAL GENERAL. El Excel tampoco agrega la columna del grupo. Lo que se
 * cuenta en "(N ...)" se nombra con `unidad` y `unidadPlural` ("credito" por defecto).
 *
 * `resumen` (opcional): `(filas) => [{ label, valor }]`. Dibuja una franja de datos
 * sueltos bajo la tabla de cada grupo y, bajo el TOTAL GENERAL, otra con todas las filas
 * (en el modo sin grupos, solo esa). Sirve para cifras que no son la suma de una columna
 * (conteos por tipo, porcentajes). Sin `resumen` no se dibuja nada.
 *
 * Traido de InverPresta_Dimas (764c27a2). Aqui se agregaron `unaVezPorId`, el
 * orden por columna, el conteo de creditos distintos y celdas mas angostas para
 * que quepan 15 columnas.
 */

// Sin repetir creditos: la primera fila de cada id.
const unaPorId = (filas) => {
	const vistos = new Set();
	return filas.filter((fila) => {
		if (vistos.has(fila.id)) return false;
		vistos.add(fila.id);
		return true;
	});
};

const celdasAngostas = { '& .MuiTableCell-root': { px: 0.75 } };
const ReporteAgrupado = ({
	tituloReporte,
	nombreArchivo,
	parametros,
	columnas,
	data,
	agruparPor,
	etiquetaGrupo = 'Gestor',
	unidad = 'credito',
	unidadPlural = 'creditos',
	ordenInicial,
	resumen,
	contexto
}) => {
	const compRef = useRef();
	const { user, rol } = contexto;
	// El modo sin grupos lo decide el reporte (sin agruparPor), no un nombre nulo en los datos.
	const conGrupos = Boolean(agruparPor);

	const campoOrden = (col) => col.orden ?? col.key;
	const { orden, cambiarOrden, ordenar } = useOrdenTabla(
		ordenInicial ?? campoOrden(columnas[0]),
		columnas.filter((col) => col.texto).map(campoOrden)
	);

	const columnasTotalizadas = useMemo(() => columnas.filter((col) => col.total), [columnas]);

	// Las columnas anteriores a la primera totalizada se fusionan en la celda con
	// la etiqueta del total; de ahi en adelante cada columna lleva su suma.
	const primeraTotalizada = useMemo(
		() => columnas.findIndex((col) => col.total),
		[columnas]
	);
	const columnasPie = useMemo(
		() => columnas.slice(primeraTotalizada),
		[columnas, primeraTotalizada]
	);

	const sumar = useMemo(
		() => (filas) =>
			columnasTotalizadas.reduce((acumulado, col) => {
				const sumadas = col.unaVezPorId ? unaPorId(filas) : filas;
				acumulado[col.key] = sumadas.reduce((suma, f) => suma + (f[col.key] ?? 0), 0);
				return acumulado;
			}, {}),
		[columnasTotalizadas]
	);

	// Los totales se derivan de los datos y no se acumulan durante el render, para
	// que volver a renderizar no los duplique.
	const grupos = useMemo(() => {
		if (!agruparPor) {
			return [{ nombre: null, filas: data, totales: sumar(data) }];
		}
		const porGrupo = new Map();
		data.forEach((fila) => {
			const nombre = agruparPor(fila);
			if (!porGrupo.has(nombre)) {
				porGrupo.set(nombre, []);
			}
			porGrupo.get(nombre).push(fila);
		});

		return Array.from(porGrupo, ([nombre, filas]) => ({
			nombre,
			filas,
			totales: sumar(filas)
		})).sort((a, b) => a.nombre.localeCompare(b.nombre));
	}, [data, agruparPor, sumar]);

	const totalGeneral = useMemo(() => sumar(data), [data, sumar]);

	const exportExcel = () => {
		const filas = data.map((fila) => {
			const salida = agruparPor ? { [etiquetaGrupo]: agruparPor(fila) } : {};
			columnas.forEach((col) => {
				if (col.excelColumnas) {
					Object.assign(salida, col.excelColumnas(fila));
					return;
				}
				salida[col.label] = col.excel ? col.excel(fila) : col.valor(fila);
			});
			return salida;
		});

		const libro = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(libro, XLSX.utils.json_to_sheet(filas), 'Reporte');
		XLSX.writeFile(libro, `${nombreArchivo}.xlsx`);
	};

	const FilaTotales = ({ etiqueta, totales, resaltado, impresion }) => (
		<TableRow>
			<TableCell
				colSpan={primeraTotalizada}
				align="right"
				sx={{ fontWeight: 'bold', fontSize: 10, ...(impresion && { color: '#000' }) }}
			>
				{etiqueta}
			</TableCell>
			{columnasPie.map((col) => (
				<TableCell
					key={col.key}
					align={col.align}
					sx={{
						fontWeight: 'bold',
						fontSize: 10,
						color: impresion ? '#000' : resaltado ? 'primary.main' : 'text.primary'
					}}
				>
					{col.total
						? col.formatoTotal
							? col.formatoTotal(totales[col.key])
							: totales[col.key]
						: ''}
				</TableCell>
			))}
		</TableRow>
	);

	// Las tablas de los grupos y el total general. Pantalla y papel salen de aqui, asi
	// que no pueden diferir; en papel todo va en negro y la flecha de orden la oculta
	// helpers/OrdenTabla.
	// El total general conserva su texto de siempre ("1 creditos") con la unidad por defecto;
	// con otra unidad si distingue el singular.
	const contados = (filas, singular = true) =>
		`${unaPorId(filas).length} ${
			unaPorId(filas).length === 1 && singular ? unidad : unidadPlural
		}`;

	// Franja de cifras sueltas del reporte (prop `resumen`), con el mismo estilo de los totales.
	const franjaResumen = (filas, titulo, impresion) => (
		<Stack
			direction="row"
			flexWrap="wrap"
			justifyContent="space-around"
			gap="6px 18px"
			sx={{ marginTop: '8px', textAlign: 'center' }}
		>
			{titulo ? (
				<Typography
					sx={{ fontWeight: 'bold', ...(impresion && { color: '#000' }) }}
					fontSize={10}
				>
					{titulo}
				</Typography>
			) : null}
			{resumen(filas).map((dato) => (
				<Stack key={dato.label} textAlign="center">
					<Typography
						sx={{
							fontWeight: 'bold',
							color: impresion ? '#000' : 'primary.main'
						}}
						fontSize={10}
					>
						{dato.label}
					</Typography>
					<Typography
						sx={{ color: impresion ? '#000' : 'text.primary' }}
						fontSize={10}
					>
						{dato.valor}
					</Typography>
				</Stack>
			))}
		</Stack>
	);

	const tablas = (impresion) =>
		isEmpty(data) ? (
			<NoData />
		) : (
			<>
				{grupos.map((grupo, indice) => (
					<Stack key={grupo.nombre ?? 'todos'} sx={{ marginBottom: '18px' }}>
						{!conGrupos ? null : (
							<>
								<Stack direction="row" spacing={1}>
									<Typography
										sx={{ color: impresion ? '#000' : 'text.secondary', textAlign: 'left' }}
										fontSize={11}
									>
										{`${etiquetaGrupo}:`}
									</Typography>
									<Typography
										sx={{ textAlign: 'left', fontWeight: 'bold', ...(impresion && { color: '#000' }) }}
										fontSize={11}
									>
										{`${grupo.nombre} (${contados(grupo.filas)})`}
									</Typography>
								</Stack>

								<Divider />
							</>
						)}

						<TableContainer>
							<Table size="small" sx={celdasAngostas}>
								<TableHead>
									<TableRow>
										{columnas.map((col) =>
											col.ordenable === false ? (
												<TableCell
													key={col.key}
													align={col.align}
													className={impresion ? 'fw-bold' : 'text-primary fw-bold'}
													style={{ fontSize: 10, ...(impresion && { color: '#000' }) }}
												>
													{col.label}
												</TableCell>
											) : (
												<EncabezadoOrdenable
													key={col.key}
													campo={campoOrden(col)}
													orden={orden}
													cambiarOrden={cambiarOrden}
													align={col.align}
													className={impresion ? 'fw-bold' : 'text-primary fw-bold'}
													style={{ fontSize: 10, ...(impresion && { color: '#000' }) }}
												>
													{col.label}
												</EncabezadoOrdenable>
											)
										)}
									</TableRow>
								</TableHead>
								<TableBody>
									{ordenar(grupo.filas).map((fila) => (
										<TableRow key={fila.id}>
											{columnas.map((col) => (
												<TableCell
													key={col.key}
													align={col.align}
													style={{ fontSize: 9, ...(impresion && { color: '#000' }) }}
												>
													{col.valor(fila)}
												</TableCell>
											))}
										</TableRow>
									))}
									{!conGrupos ? null : (
										<FilaTotales
											etiqueta={`Total ${grupo.nombre}:`}
											totales={grupo.totales}
											impresion={impresion}
										/>
									)}
									{/* El total general va en la tabla del último grupo: en una
									    tabla aparte sus columnas no quedaban alineadas. */}
									{indice === grupos.length - 1 ? (
										<FilaTotales
											etiqueta={`TOTAL GENERAL (${contados(data, unidad !== 'credito')}):`}
											totales={totalGeneral}
											resaltado
											impresion={impresion}
										/>
									) : null}
								</TableBody>
							</Table>
						</TableContainer>
						{/* Con un solo grupo, su franja sería idéntica a la del TOTAL GENERAL. */}
						{resumen && conGrupos && grupos.length > 1 ? franjaResumen(grupo.filas, null, impresion) : null}
						{resumen && indice === grupos.length - 1
							? franjaResumen(data, conGrupos ? 'TOTAL GENERAL' : null, impresion)
							: null}
					</Stack>
				))}
			</>
		);

	return (
		<div>
			<Dialog fullScreen open={true} PaperProps={{ 'aria-label': tituloReporte }}>
				<EncabezadoReporte
					titulo={tituloReporte}
					parametros={parametros}
					onExportar={exportExcel}
					impresionRef={compRef}
					margenMm={5}
					usuario={user}
					rol={rol}
				/>

				<Box sx={{ paddingX: '22px', textAlign: 'center' }}>
					<Divider sx={{ marginY: '14px' }} />
					{tablas(false)}
				</Box>
			</Dialog>

			<div style={{ display: 'none' }}>
				<HojaImpresion
					ref={compRef}
					titulo={tituloReporte}
					parametros={parametros}
					usuario={user}
					rol={rol}
				>
					<Box sx={{ textAlign: 'center' }}>{tablas(true)}</Box>
				</HojaImpresion>
			</div>
		</div>
	);
};

export default ReporteAgrupado;
